import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import clsx from 'clsx';
import {
  Button,
  DatePicker,
  ErrorContainer,
  LabelComponent,
  LoadingSpinner,
  Stack,
  TimePicker
} from 'components';
import { useFormik, getIn, setIn } from 'formik';
import debounce from 'lodash.debounce';
import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  getScreenerDetails,
  getScreenerHistory,
  getScreeners,
  patchScreener,
  putScreener
} from 'store/screeener/screenerActions';
import { Answers, ScreenerDetails } from 'store/screeener/screenerSlice';
import dayjs from 'dayjs';
import * as yup from 'yup';

import UpdatedLast from '../components/updatedLast';
import style from './impact.module.scss';
import ImpactFooter from './impactFooter';

// This function is needed to remap the anwsers for the impact form
// since we are using setIn from formik to set the answer by its id in an nested object
// and the setIn function if it encounters a number it will create an array which doesn't work for our usecase
// so instead of doing this:
// const answers = {
//   1: {
//     1: {
//       answer: ['Yes'],
//     }
//   }
// };
// it does this
// const answers = {
//   1: [
//     undefined,
//     {
//       answer: ['Yes'],
//     }
//   ]
// };
// which is not correct and would throw an error from the BE
// so this function just converts it to the correct format

const remapAnswers = (answers: Answers) => {
  return (Object.keys(answers) as unknown as number[]).reduce((sectionAcc, sectionKey) => {
    sectionAcc[sectionKey] = (Object.keys(answers[sectionKey]) as unknown as number[]).reduce(
      (questionAcc, questionKey) => {
        questionAcc[questionKey] = answers[sectionKey][questionKey];

        return questionAcc;
      },
      {} as Record<string, any>
    );
    return sectionAcc;
  }, {} as Record<string, any>);
};

export type Props = {
  closeModal: () => void;
};

function checkIsEmpty(object: any): any {
  if (!object) {
    return true;
  }

  return Object.values(object).every((v) =>
    v && typeof v === 'object' ? checkIsEmpty(v) : v === null || v === ''
  );
}

const Impact = ({ closeModal }: Props) => {
  const { clientId = '' } = useParams();
  const dispatch = useAppDispatch();
  const impactQuestionnare = useAppSelector(
    (state) => state.screener[clientId]?.questionnare?.impact
  );
  const impactScreener = useAppSelector((state) => state.screener[clientId]?.screener?.impact);
  const [lastSaved, setLastSaved] = useState<string | null>(null);
  const [isAutosaving, setIsAutosaving] = useState(false);

  console.log(impactScreener?.data);

  console.log(
    'time:',
    dayjs(impactScreener?.data?.dateAndTimeOfScreening).format('DD/MM/YYYY HH:mm')
  );

  const formik = useFormik({
    initialValues: {
      dateOfScreening: impactScreener?.data?.dateAndTimeOfScreening
        ? dayjs(impactScreener?.data?.dateAndTimeOfScreening).format('YYYY-MM-DD')
        : impactScreener?.data?.dateOfScreening,
      timeOfScreening: impactScreener?.data?.dateAndTimeOfScreening
        ? dayjs(impactScreener?.data?.dateAndTimeOfScreening).format('HH:mm')
        : impactScreener?.data?.timeOfScreening
        ? dayjs(`${dayjs().format('YYYY-MM-DD')}T${impactScreener?.data?.timeOfScreening}Z`).format(
            'HH:mm'
          )
        : null,
      answers: impactScreener?.data?.answers ?? null
    },
    validateOnMount: true,
    validationSchema: yup.object().shape({
      dateOfScreening: yup
        .string()
        .nullable()
        .required('Date of Screening is required.')
        .test('dateOfScreening', 'Date cannot be in the future.', (value) => {
          if (!value) {
            return true;
          }
          const now = dayjs();

          const isInFuture = dayjs(value).isAfter(now, 'day');

          return !isInFuture;
        }),
      timeOfScreening: yup.string().nullable().required('Time of Screening is required.')
    }),
    onSubmit: async (values) => {
      if (values.answers === null || !formik.isValid) {
        return;
      }
      const response = await dispatch(
        patchScreener({
          clientId,
          type: 'impact',
          screener: { ...values, answers: remapAnswers(values.answers), submitted: true }
        })
      );
      if (response.meta.requestStatus === 'fulfilled') {
        dispatch(
          getScreenerHistory({
            clientId,
            type: 'impact'
          })
        );
        dispatch(getScreenerDetails({ clientId, type: 'impact' }));
        closeModal();
      }
    }
  });

  const isEmpty = checkIsEmpty(formik.values.answers);

  const autosaveForm = async (values: ScreenerDetails) => {
    setIsAutosaving(true);
    const response = await dispatch(putScreener({ clientId, type: 'impact', screener: values }));
    setIsAutosaving(false);
    if (response.meta.requestStatus === 'fulfilled') {
      setLastSaved(new Date().toISOString());
    }
    dispatch(getScreeners({ clientId }));
    dispatch(getScreenerDetails({ clientId, type: 'impact' }));
  };

  const debounceFn = useCallback(debounce(autosaveForm, 900), []);

  const handleChange = async ({
    name,
    value
  }: {
    name: string;
    value: string | string[] | null;
  }) => {
    formik.setFieldValue(name, value);

    const values: {
      answers: Answers;
      dateOfScreening: string | null;
      timeOfScreening: string | null;
    } = setIn(formik.values, name, value);

    // we call the autosave function here to avoid the mess with useEffect
    // so we only trigger autosave when some value changes in the whole form
    debounceFn({ ...values, answers: values.answers ? remapAnswers(values.answers) : null });
  };

  const loading = formik.isSubmitting || isAutosaving;
  const submitDisabled = isEmpty || loading || impactScreener?.data?.submitted || !formik.isValid;

  return (
    <form style={{ height: '100%' }} onSubmit={formik.handleSubmit}>
      <Stack height="100%">
        <Stack height="100%" overflow="auto" minHeight={0} pb="24px">
          <Stack display="flex" gap="16px">
            <Stack flexDirection="row" gap="16px">
              <Stack width="100%">
                <LabelComponent text="Date of Screening" mandatory />
                <DatePicker
                  value={formik.values.dateOfScreening}
                  onChange={(date: string) =>
                    handleChange({ name: 'dateOfScreening', value: date })
                  }
                  disableFuture
                  onBlur={() =>
                    formik.setFieldTouched('dateOfScreening', true).then(() => {
                      formik.validateForm();
                    })
                  }
                />
                <ErrorContainer
                  errors={formik.errors}
                  visible={formik.errors.dateOfScreening && formik.touched.dateOfScreening}
                  name="dateOfScreening"
                />
              </Stack>
              <Stack width="100%" marginRight="2px">
                <LabelComponent text="Time of Screening" mandatory />
                <TimePicker
                  classnamesProps={style.timePicker}
                  name="timeOfScreening"
                  onChange={(time: string) => {
                    if (time === 'Invalid Date') {
                      formik.setFieldValue('timeOfScreening', null).then(() => {
                        formik.setFieldTouched('timeOfScreening', true);
                      });
                    } else {
                      handleChange({ name: 'timeOfScreening', value: time }).then(() => {
                        formik.setFieldTouched('timeOfScreening', true);
                      });
                    }
                  }}
                  onBlur={() => {
                    formik.setFieldTouched('timeOfScreening').then(() => {
                      formik.validateForm();
                    });
                  }}
                  value={formik.values.timeOfScreening}
                />
                <ErrorContainer
                  errors={formik.errors}
                  visible={formik.errors.timeOfScreening && formik.touched.timeOfScreening}
                  name="timeOfScreening"
                />
              </Stack>
            </Stack>
            {impactQuestionnare?.map((section, sectionIndex) => (
              <Stack display="flex" gap="12px" key={section.id}>
                <p className={style.sectionTitle}>{section.title}</p>
                <Stack display="flex" gap="16px">
                  {section.questions.map((question) => (
                    <Stack display="flex" gap="8px" key={question.id}>
                      <p
                        className={style.questionTitle}
                        // questions can have line breaks (<br />) so we need to do this in order to render them
                        dangerouslySetInnerHTML={{
                          __html: question.title
                        }}
                      />
                      <Stack display="flex">
                        <ToggleButtonGroup
                          sx={{
                            height: 'auto',
                            maxHeight: '600px',
                            transition: 'height 0.3s ease-out',
                            flexDirection: 'row',
                            marginBottom: '16px',
                            '&.MuiToggleButtonGroup-root': {
                              width: '100%',
                              flexWrap: 'wrap'
                            },
                            '&.MuiToggleButtonGroup-root > * + *': {
                              marginLeft: '4px !important'
                            },
                            '&.MuiToggleButtonGroup-root div': {
                              width: '100%'
                            }
                          }}
                          value={getIn(
                            formik.values,
                            `answers.${section.id}.${question.id}.answers`
                          )}
                          exclusive={!question.multiselect}
                          onChange={(_, value) => {
                            if (question.multiselect) {
                              handleChange({
                                name: `answers.${section.id}.${question.id}.answers`,
                                value
                              });
                            } else {
                              const currentVal = getIn(
                                formik.values,
                                `answers.${section.id}.${question.id}.answers`
                              );

                              handleChange({
                                name: `answers.${section.id}.${question.id}.answers`,
                                value: value === currentVal?.[0] ? null : [value]
                              });
                            }
                          }}
                          aria-label="Client status"
                        >
                          {question.answers.map((answer) => (
                            <ToggleButton
                              key={answer.id}
                              sx={{
                                px: '16px',
                                py: '12px',
                                border: '1px solid #CBD4E1 !important',
                                borderRadius: '4px !important',
                                color: '#314157',
                                textTransform: 'none',
                                marginTop: '4px',
                                '&.MuiToggleButton-root:hover:not(.Mui-selected)': {
                                  backgroundColor: 'transparent'
                                },
                                '&.MuiToggleButton-root:hover': {
                                  backgroundColor: '#EFF9FB'
                                },
                                '&.Mui-selected': {
                                  backgroundColor: '#EFF9FB',
                                  color: '#096F84',
                                  border: '1px solid #5EBBCE !important'
                                }
                              }}
                              value={answer.id}
                            >
                              <span>{answer.text}</span>
                            </ToggleButton>
                          ))}
                        </ToggleButtonGroup>
                        <div
                          data-replicated-value={getIn(
                            formik.values,
                            `answers.${section.id}.${question.id}.note`
                          )}
                          className={clsx(style.growWrap)}
                        >
                          <textarea
                            rows={1}
                            id="text"
                            placeholder="Click to add a note"
                            className={style.noteTextarea}
                            name={`answers.${section.id}.${question.id}.note`}
                            onChange={(e) => {
                              handleChange({
                                name: `answers.${section.id}.${question.id}.note`,
                                value: e.target.value
                              });
                            }}
                            onBlur={formik.handleBlur}
                            value={getIn(
                              formik.values,
                              `answers.${section.id}.${question.id}.note`
                            )}
                          />
                        </div>
                      </Stack>
                    </Stack>
                  ))}
                </Stack>
                <div className={style.separator} />
              </Stack>
            ))}
          </Stack>
        </Stack>
        <ImpactFooter>
          <Stack display="flex" flexDirection="row" alignItems="center" gap="16px">
            {loading ? (
              <LoadingSpinner size={30} />
            ) : (
              <UpdatedLast lastSaved={lastSaved} updatedAt={impactScreener.data?.updatedAt} />
            )}
            <Stack width="125px">
              <Button disabled={submitDisabled} text="Submit" theme="primary" />
            </Stack>
          </Stack>
        </ImpactFooter>
      </Stack>
    </form>
  );
};

export default Impact;
